import ChevronRight from '@mui/icons-material/ChevronRightRounded';
import {
  Box,
  ClickAwayListener,
  Grow,
  MenuList,
  Paper,
  Popper,
  MenuItem as MUIMenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState, useRef, useCallback } from 'react';
import type {
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from 'react';

// This function checks whether a point (x, y) is on the left or right side of a line formed by two points (px, py) and (qx, qy).
// If the result is negative, the point is on the right side of the line. If positive, it's on the left side.
// It helps us determine if a point is on the same side as a vertex of the triangle when compared to its edges.
function sign(
  px: number,
  py: number,
  qx: number,
  qy: number,
  rx: number,
  ry: number,
) {
  return (px - rx) * (qy - ry) - (qx - rx) * (py - ry);
}

// This function checks if a point (x, y) is inside a triangle formed by three points (x1, y1), (x2, y2), and (x3, y3).
function pointInTriangle(
  currentMouseCoordinates: Array<number>,
  triangleCoordinates: Array<Array<number>>,
) {
  const [[x1, y1], [x2, y2], [x3, y3]] = triangleCoordinates;
  const [x, y] = currentMouseCoordinates;

  const b1 = sign(x, y, x1, y1, x2, y2) <= 0;
  const b2 = sign(x, y, x2, y2, x3, y3) <= 0;
  const b3 = sign(x, y, x3, y3, x1, y1) <= 0;
  // If all signs are the same (either all negative or all positive), the point is inside the triangle.
  return b1 === b2 && b2 === b3;
}

const StyledMenuItem = styled(MUIMenuItem)(({ theme }) => ({
  fontSize: '14px',
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  height: 36,

  '&.Mui-selected': {
    fontWeight: 400,
  },
}));

export interface MenuItemWithOptionsProps {
  /** The label to display in the menu item */
  label: ReactNode;
  /** Icon to display before the label */
  icon?: ReactNode;
  /** Nested options as children components (e.g., MenuItem elements) */
  children: ReactNode;
  /** Whether the menu item is disabled */
  disabled?: boolean;
  /** Whether this menu item is selected */
  selected?: boolean;
  /** Whether to show a divider below this item */
  divider?: boolean;
  /** Placement of the submenu */
  placement?: 'right-start' | 'right-end' | 'left-start' | 'left-end';
}

export function MenuItemWithOptions({
  label,
  icon,
  children,
  disabled = false,
  selected = false,
  divider = false,
  placement = 'right-start',
}: Readonly<MenuItemWithOptionsProps>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuItemRef = useRef<HTMLLIElement>(null);
  const mouseLeftCoordinates = useRef<Array<number>>([]);
  const mouseIdleTimer = useRef<NodeJS.Timeout | null>(null);
  const mouseEntered = useRef(false);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (event: ReactMouseEvent<HTMLElement> | ReactKeyboardEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget as HTMLElement);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    mouseEntered.current = false;
  }, []);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        menuItemRef.current &&
        event.composedPath().includes(menuItemRef.current)
      ) {
        return;
      }
      handleClose();
    },
    [handleClose],
  );

  // Reusing the same pattern as SubMenu component for handling mouse movements
  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      let shouldComputeSubMenuOpenLogic = true;
      const submenu = document.querySelector('#menu-item-options-submenu');

      function computeSubMenuLogic() {
        if (!mouseEntered.current) {
          mouseEntered.current = true;
          handleOpen(event);
        }
      }

      if (mouseLeftCoordinates.current.length > 0 && submenu) {
        const { x, y, height } = submenu.getBoundingClientRect();

        // Form a virtual triangle using the left mouse coordinates and the top-left and bottom-left coordinates of the submenu.
        // If the current mouse coordinates fall within this triangle, skip the submenu logic computation.
        // Check https://twitter.com/diegohaz/status/1283558204178407427 for more context.
        const currentMouseCoordinates = [event.clientX, -event.clientY];
        const virtualTriangleCoordinates = [
          [x, -y],
          [x, -(y + height)],
          [mouseLeftCoordinates.current[0], mouseLeftCoordinates.current[1]],
        ];

        if (
          pointInTriangle(currentMouseCoordinates, virtualTriangleCoordinates)
        ) {
          shouldComputeSubMenuOpenLogic = false;
          if (mouseIdleTimer.current) {
            clearTimeout(mouseIdleTimer.current);
          }

          // if mouse is inside triangle and yet hasn't moved, we need to compute submenu logic after a delay
          mouseIdleTimer.current = setTimeout(() => {
            computeSubMenuLogic();
          }, 50);
        }
      }

      if (shouldComputeSubMenuOpenLogic) {
        if (mouseIdleTimer.current) {
          clearTimeout(mouseIdleTimer.current);
        }
        computeSubMenuLogic();
      }
    },
    [handleOpen],
  );

  const handleMouseLeave = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      mouseLeftCoordinates.current = [event.clientX, -event.clientY];

      if (mouseIdleTimer.current) {
        clearTimeout(mouseIdleTimer.current);
      }
      mouseEntered.current = false;

      // Schedule close after a short delay to allow moving to submenu
      mouseIdleTimer.current = setTimeout(() => {
        handleClose();
      }, 100);
    },
    [handleClose],
  );

  const handleSubmenuMouseLeave = useCallback(() => {
    if (mouseIdleTimer.current) {
      clearTimeout(mouseIdleTimer.current);
    }
    handleClose();
  }, [handleClose]);

  const handleSubmenuMouseEnter = useCallback(() => {
    // Cancel any pending close when entering submenu
    if (mouseIdleTimer.current) {
      clearTimeout(mouseIdleTimer.current);
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        handleOpen(event);
      }
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose, handleOpen],
  );

  const handleSubmenuKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowLeft' || event.key === 'Escape') {
        handleClose();
        menuItemRef.current?.focus();
      }
    },
    [handleClose],
  );

  return (
    <>
      <StyledMenuItem
        ref={menuItemRef}
        disabled={disabled}
        selected={selected}
        divider={divider}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onKeyDown={handleKeyDown}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            {label}
          </Box>
          <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
        </Box>
      </StyledMenuItem>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        sx={{ zIndex: 1302 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement.startsWith('right')
                ? 'left top'
                : 'right top',
            }}
          >
            <Paper
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                minWidth: 160,
              }}
              onMouseEnter={handleSubmenuMouseEnter}
              onMouseLeave={handleSubmenuMouseLeave}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList
                  disablePadding
                  autoFocusItem={open}
                  id="menu-item-options-submenu"
                  onKeyDown={handleSubmenuKeyDown}
                  sx={{ p: 1, maxHeight: '250px', overflowY: 'auto' }}
                >
                  {children}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
